import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasPerm } from "@/lib/constants";
import { fmtDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, FlaskConical, Loader2, Clock, RotateCcw } from "lucide-react";

const UNITS = ["g", "ml", "vol", "pz"];
const CUSTOM = "__custom__";
let _rowSeq = 0;
const rid = () => `ing-${++_rowSeq}`;
const emptyRow = () => ({ _uid: rid(), product_id: "", product_name: "", qty: "", unit: "g" });
const emptyForm = () => ({ technique: "", processing_min: 30, notes: "", ingredients: [emptyRow()] });

// Fase 5.2.2 — Pannello Formule Colore collegate all'appuntamento.
// Ingredienti strutturati (prodotto + dosaggio) + tempo di posa + note.
export const ColorFormulaPanel = ({ appointmentId }) => {
  const { user } = useAuth();
  const canWrite = hasPerm(user, "formulas.write");

  const [loading, setLoading] = useState(true);
  const [formulas, setFormulas] = useState([]);
  const [lastClient, setLastClient] = useState(null);
  const [products, setProducts] = useState([]);
  const [adding, setAdding] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(emptyForm());

  const load = useCallback(() => {
    if (!appointmentId) return;
    setLoading(true);
    api.get(`/appointments/${appointmentId}/formula`)
      .then((r) => { setFormulas(r.data.formulas || []); setLastClient(r.data.last_client_formula || null); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [appointmentId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (canWrite) api.get("/catalog/products").then((r) => setProducts(r.data)).catch(() => {}); }, [canWrite]);

  const setIng = (i, patch) =>
    setForm((f) => ({ ...f, ingredients: f.ingredients.map((x, idx) => (idx === i ? { ...x, ...patch } : x)) }));
  const pickProduct = (i, val) => {
    if (val === CUSTOM) { setIng(i, { product_id: CUSTOM, product_name: "", unit: "g" }); return; }
    const p = products.find((x) => x.id === val);
    setIng(i, { product_id: val, product_name: p?.name || "", unit: p?.unit || "g" });
  };
  const addRow = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, emptyRow()] }));
  const removeRow = (i) => setForm((f) => ({ ...f, ingredients: f.ingredients.length > 1 ? f.ingredients.filter((_, idx) => idx !== i) : f.ingredients }));

  const reuseLast = () => {
    if (!lastClient) return;
    setForm({
      technique: lastClient.technique || "",
      processing_min: lastClient.processing_min || 30,
      notes: lastClient.notes || "",
      ingredients: (lastClient.ingredients && lastClient.ingredients.length)
        ? lastClient.ingredients.map((x) => ({ _uid: rid(), product_id: x.product_id || CUSTOM, product_name: x.product_name || "", qty: x.qty ?? "", unit: x.unit || "g" }))
        : [emptyRow()],
    });
    setAdding(true);
  };

  const save = async () => {
    const clean = form.ingredients
      .filter((x) => (x.product_name || "").trim() || (x.product_id && x.product_id !== CUSTOM))
      .map((x) => ({ product_id: x.product_id === CUSTOM ? null : x.product_id, product_name: x.product_name, qty: x.qty === "" ? null : Number(x.qty), unit: x.unit }));
    if (!form.technique.trim() && !clean.length) { toast.error("Inserisci una tecnica o almeno un ingrediente"); return; }
    setSaving(true);
    try {
      await api.post(`/appointments/${appointmentId}/formula`, { ...form, ingredients: clean });
      toast.success("Formula colore salvata");
      setAdding(false); setForm(emptyForm()); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore durante il salvataggio"); }
    finally { setSaving(false); }
  };

  return (
    <div className="border-t border-border pt-4 mt-1" data-testid="color-formula-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FlaskConical className="h-4 w-4 text-[hsl(var(--hi-gold))]" />
          <span className="text-sm font-semibold">Formula colore</span>
          {formulas.length > 0 && <Badge variant="secondary" data-testid="formula-count">{formulas.length}</Badge>}
        </div>
        {canWrite && !adding && (
          <div className="flex items-center gap-2">
            {lastClient && (
              <Button variant="ghost" size="sm" onClick={reuseLast} data-testid="formula-reuse-last">
                <RotateCcw className="h-3.5 w-3.5 mr-1" /> Riprendi ultima
              </Button>
            )}
            <Button size="sm" onClick={() => { setForm(emptyForm()); setAdding(true); }} data-testid="formula-add-open">
              <Plus className="h-4 w-4 mr-1" /> Aggiungi
            </Button>
          </div>
        )}
      </div>

      {loading ? (
        <div className="h-16 flex items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--hi-gold))]" /></div>
      ) : (
        <div className="space-y-2">
          {formulas.length === 0 && !adding && (
            <div className="text-sm text-muted-foreground rounded-lg border border-dashed border-border p-4 text-center" data-testid="formula-empty">
              Nessuna formula registrata per questo appuntamento.
            </div>
          )}
          {formulas.map((f) => (
            <div key={f.id} className="rounded-lg border border-border p-3 bg-card" data-testid={`formula-card-${f.id}`}>
              <div className="flex items-center justify-between">
                <span className="font-medium text-sm">{f.technique || "Formula colore"}</span>
                <span className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="h-3 w-3" /> {f.processing_min || 0}′</span>
              </div>
              {(f.ingredients || []).length > 0 && (
                <ul className="mt-2 space-y-1">
                  {f.ingredients.map((ing, idx) => (
                    <li key={`${ing.product_id || ing.product_name}-${idx}`} className="flex items-center justify-between text-sm">
                      <span>{ing.product_name || "—"}</span>
                      <span className="tabular text-muted-foreground">{ing.qty != null ? ing.qty : ""} {ing.unit}</span>
                    </li>
                  ))}
                </ul>
              )}
              {f.notes && <div className="text-xs text-muted-foreground mt-2 border-t border-border pt-2">{f.notes}</div>}
              <div className="text-[11px] text-muted-foreground mt-2">{f.created_by_name || "staff"} · {fmtDate(f.date)}</div>
            </div>
          ))}

          {adding && (
            <div className="rounded-lg border border-[hsl(var(--hi-gold)/0.35)] bg-[hsl(var(--hi-gold-soft))] p-3 space-y-3" data-testid="formula-form">
              <div className="grid grid-cols-3 gap-2">
                <div className="col-span-2 space-y-1"><Label className="text-xs">Tecnica</Label>
                  <Input value={form.technique} onChange={(e) => setForm((f) => ({ ...f, technique: e.target.value }))} placeholder="Es. Balayage caramello" data-testid="formula-technique-input" />
                </div>
                <div className="space-y-1"><Label className="text-xs">Posa (min)</Label>
                  <Input type="number" min="0" value={form.processing_min} onChange={(e) => setForm((f) => ({ ...f, processing_min: e.target.value === "" ? 0 : +e.target.value }))} data-testid="formula-posa-input" />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs">Ingredienti e dosaggi</Label>
                {form.ingredients.map((ing, i) => {
                  const isCustom = ing.product_id === CUSTOM || (!ing.product_id && !!ing.product_name);
                  const selectValue = ing.product_id && ing.product_id !== CUSTOM ? ing.product_id : (isCustom ? CUSTOM : "");
                  return (
                    <div key={ing._uid} className="flex items-start gap-1.5" data-testid={`formula-ing-row-${i}`}>
                      <div className="flex-1 space-y-1">
                        <Select value={selectValue} onValueChange={(v) => pickProduct(i, v)}>
                          <SelectTrigger className="h-9" data-testid={`formula-ing-product-${i}`}><SelectValue placeholder="Prodotto" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value={CUSTOM}>Testo libero…</SelectItem>
                            {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        {isCustom && (
                          <Input value={ing.product_name} onChange={(e) => setIng(i, { product_name: e.target.value })} placeholder="Nome prodotto" className="h-8" data-testid={`formula-ing-name-${i}`} />
                        )}
                      </div>
                      <Input type="number" min="0" step="0.1" value={ing.qty} onChange={(e) => setIng(i, { qty: e.target.value })} placeholder="Dose" className="w-20 h-9" data-testid={`formula-ing-qty-${i}`} />
                      <Select value={ing.unit} onValueChange={(v) => setIng(i, { unit: v })}>
                        <SelectTrigger className="w-20 h-9" data-testid={`formula-ing-unit-${i}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{UNITS.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                      </Select>
                      <Button variant="ghost" size="icon" className="h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive" onClick={() => removeRow(i)} data-testid={`formula-ing-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  );
                })}
                <Button variant="outline" size="sm" onClick={addRow} className="w-full" data-testid="formula-ing-add"><Plus className="h-3.5 w-3.5 mr-1" /> Aggiungi ingrediente</Button>
              </div>

              <div className="space-y-1"><Label className="text-xs">Note</Label>
                <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))} placeholder="Note sul risultato, applicazione, ecc." className="min-h-[60px]" data-testid="formula-notes-input" />
              </div>

              <div className="flex gap-2">
                <Button className="flex-1" onClick={save} disabled={saving} data-testid="formula-save-btn">
                  {saving ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : null} Salva formula
                </Button>
                <Button variant="outline" onClick={() => { setAdding(false); setForm(emptyForm()); }} data-testid="formula-cancel-btn">Annulla</Button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
